// ============================================================
// boton-secundario.js — el BOTÓN SECUNDARIO de los bloques con botón
// (19-10-2026; decisiones del dueño D-1200 a D-1203: aspecto A —contorno del color del texto—, en los
// bloques que ya tienen botón, solo a mano —la IA no lo propone— y se añade con «+ Secundario» en la
// barra del botón, editor/boton-panel.js).
//
// Módulo PURO (sin DOM ni `window`; ES2019, sin `?.` ni `??`): lo importan los renderers (bundle
// público incluido) y el editor.
//
// EL DATO. `cta2` (texto) + `cta2_url` (enlace) en el mismo objeto que el `cta` principal: los props
// del bloque o, en el carrusel, cada diapositiva (`slides[i].cta2`). El extractor los lee como a
// cualquier botón (`data-field="cta2"` → `cta2`, su `data-link` → `cta2_url`), «Estilo del botón» lo
// guarda en `botones.cta2` y el traductor lo trata como copy (`utils/translatable.js`). Quitarlo borra
// los tres.
//
// LO PUBLICADO SIN SECUNDARIO SALE BYTE A BYTE COMO ANTES: la fila `.n3i-botones` que agrupa los dos
// botones solo existe si hay `cta2` (`conSecundario` devuelve el botón principal tal cual).
//
// EL ENLACE no hereda el destino global de los botones (`destinoGlobal: false`, utils/links.js): ése es
// la acción principal del sitio. Sin enlace propio, `destino-boton.js` lo lleva a donde dice su texto.
// ============================================================

import { ctaAnchorAttrs } from './links.js';
import { estiloDeBoton, CAMPOS_SECUNDARIOS } from './boton-estilo.js';

/** El campo del secundario (`cta2`); su enlace es `cta2_url` y su estilo propio, `botones.cta2`. */
export const CAMPO_SECUNDARIO = CAMPOS_SECUNDARIOS[0];

/**
 * Los tipos de bloque cuyo render pinta el secundario: los que ofrecen «+ Secundario» en la barra del
 * botón. Un tipo entra aquí el mismo día que su render llama a `conSecundario` (lo comprueba
 * tests/test_boton_secundario.py).
 */
export const BLOQUES_CON_SECUNDARIO = ['hero', 'content', 'banner', 'cta-bar', 'video', 'imagen', 'countdown', 'carrusel'];

/** Texto visible de un campo de botón (sin etiquetas ni espacios duros). */
function textoPlano(v) {
    return String(v).replace(/<[^>]*>/g, ' ').replace(/&nbsp;|\u00a0/gi, ' ').trim();
}

/** ¿Este objeto (props o una diapositiva) tiene botón secundario? Un texto vacío no lo es. */
export function tieneSecundario(dueno) {
    const v = dueno ? dueno[CAMPO_SECUNDARIO] : null;
    return typeof v === 'string' && textoPlano(v) !== '';
}

/**
 * El `<a>` del secundario.
 * @param {Object} props  props del bloque (las de `safe()` valen: no lee colores).
 * @param {Object} [o]
 * @param {Object} [o.item]      la tarjeta dueña (una diapositiva del carrusel); sin ella, los props.
 * @param {string} [o.boton]     declaraciones CSS FIJAS del render para el secundario (el mismo tamaño y
 *                               relleno que su principal; `--text` donde el texto del bloque no es el de la
 *                               sección). Nunca datos del cliente: van tal cual al `style`.
 * @param {boolean} [o.editable] false = copia NO editable (la barra fija del cta-bar en «Ver sitio»).
 * @param {string} [o.clase]     clases del `<a>` (por defecto `btn-action btn-secundario`).
 */
export function botonSecundario(props, o) {
    const op = o || {};
    const d = op.item || props;
    const editable = op.editable !== false;
    const estilo = estiloDeBoton(props, CAMPO_SECUNDARIO, op.item) + (op.boton || '');
    return `<a ${ctaAnchorAttrs(d.cta2_url, { destinoGlobal: false, editable })} class="${op.clase || 'btn-action btn-secundario'}"`
        + (editable ? ' data-field="cta2" contenteditable="true"' : '')
        + (estilo ? ` style="${estilo}"` : '') + `>${d.cta2}</a>`;
}

/**
 * El botón principal (su HTML ya pintado) y, si hay secundario, los dos en la fila `.n3i-botones`.
 * Sin secundario devuelve `principal` sin tocar un byte. Si el principal está vacío (el render no lo
 * pinta sin texto) y hay secundario, la fila lleva solo el secundario: así se puede seguir quitando.
 * @param {string} principal  el HTML del botón principal ('' si el render no lo pinta).
 * @param {Object} props      props del bloque.
 * @param {Object} [o]        las de `botonSecundario`, más `fila`: declaraciones CSS FIJAS del render
 *                            para la fila (el margen y la alineación que llevaba el principal).
 */
export function conSecundario(principal, props, o) {
    const op = o || {};
    if (!tieneSecundario(op.item || props)) return principal;
    return `<div class="n3i-botones"${op.fila ? ` style="${op.fila}"` : ''}>${principal}${botonSecundario(props, op)}</div>`;
}

/**
 * Lo que escribe «Quitar»: borra el texto, el enlace y el estilo propio del secundario (y `botones`
 * entero si se queda vacío). Devuelve el parche para `guardarPropsDeBloque` (null = borrar la clave).
 */
export function parcheSinSecundario(dueno) {
    const parche = { cta2: null, cta2_url: null };
    const b = dueno && dueno.botones;
    if (b && typeof b === 'object' && !Array.isArray(b) && Object.prototype.hasOwnProperty.call(b, CAMPO_SECUNDARIO)) {
        const resto = {};
        Object.keys(b).forEach(k => { if (k !== CAMPO_SECUNDARIO) resto[k] = b[k]; });
        parche.botones = Object.keys(resto).length ? resto : null;
    }
    return parche;
}
